if (typeof MenuDrawerAccordion === 'undefined') {
  class MenuDrawerAccordion {
    constructor() {
      this.init();
      this.activeAccordion = null;
      this.animationInProgress = false;
    }

    init() {
      // Wait a bit to ensure DOM is fully loaded
      setTimeout(() => {
        this.setupAccordionMenuItems();
      }, 100);
    }

    setupAccordionMenuItems() {
      // Get all accordion menu items
      const accordionItems = document.querySelectorAll('.accordion-menu-item');
      
      accordionItems.forEach(item => {
        const summary = item.querySelector('summary');
        const submenu = item.querySelector('.menu-drawer__submenu');
        
        if (summary && submenu) {
          // Add accordion classes if they don't exist
          if (!submenu.classList.contains('menu-drawer__submenu--accordion')) {
            submenu.classList.add('menu-drawer__submenu--accordion');
          }
          
          if (!summary.classList.contains('menu-drawer__menu-item--accordion')) {
            summary.classList.add('menu-drawer__menu-item--accordion');
          }
          
          // Add initial aria-expanded attribute
          summary.setAttribute('aria-expanded', 'false');
          
          // Make sure icon-caret is present and properly positioned
          const caretIcon = summary.querySelector('.accordion-icon');
          if (caretIcon) {
            caretIcon.setAttribute('aria-hidden', 'true');
            
            // Fix icon display
            const iconClosed = caretIcon.querySelector('.icon-closed');
            const iconOpen = caretIcon.querySelector('.icon-open');
            
            if (iconClosed && iconOpen) {
              // Ensure proper display of icons based on open state
              if (item.hasAttribute('open')) {
                iconClosed.style.display = 'block';
                iconClosed.style.opacity = '1';
                iconOpen.style.display = 'none';
                iconOpen.style.opacity = '0';
              } else {
                iconClosed.style.display = 'none';
                iconClosed.style.opacity = '0';
                iconOpen.style.display = 'block';
                iconOpen.style.opacity = '1';
              }
            }
          }
          
          // Remove any event listeners by cloning and replacing
          const newSummary = summary.cloneNode(true);
          summary.parentNode.replaceChild(newSummary, summary);
          
          // Add our click handler to the new summary
          newSummary.addEventListener('click', (e) => this.handleSummaryClick(e));
        }
      });
      
      // Add bottom border to non-accordion menu items
      const standardMenuItems = document.querySelectorAll('.menu-drawer__menu > li > a.menu-drawer__menu-item');
      standardMenuItems.forEach(item => {
        if (!item.style.borderBottom) {
          item.style.borderBottom = '1px solid rgba(var(--color-foreground), 0.08)';
          item.style.display = 'block';
        }
      });
      
      // Make sure all menu items start visible (for main navigation)
      const mainMenuItems = document.querySelectorAll('.menu-drawer__navigation > .menu-drawer__menu > li');
      mainMenuItems.forEach(item => {
        item.style.opacity = '1';
        item.style.transform = 'none';
      });
    }
    
    closeAllAccordions(exceptAccordion = null) {
      const allAccordions = document.querySelectorAll('.accordion-menu-item[open]');
      allAccordions.forEach(accordion => {
        if (accordion !== exceptAccordion) {
          const summary = accordion.querySelector('summary');
          const submenu = accordion.querySelector('.menu-drawer__submenu');
          
          if (submenu) {
            // Start the fade out animation for all submenu items with staggered order
            const menuItems = submenu.querySelectorAll('.menu-drawer__menu > li');
            
            menuItems.forEach((item, index) => {
              // Add animation-out class to trigger CSS animations
              item.classList.add('menu-item-exiting');
              
              // Reverse the animation order for fade out (last item first)
              const delay = 0.03 * (menuItems.length - index - 1);
              item.style.transitionDelay = `${delay}s`;
              item.style.opacity = '0';
              item.style.transform = 'translateY(8px)';
            });
            
            // After a short delay to allow for animations, close the accordion
            setTimeout(() => {
              submenu.classList.remove('is-open');
              if (summary) {
                summary.setAttribute('aria-expanded', 'false');
                
                // Update icon display with fade transition
                const caretIcon = summary.querySelector('.accordion-icon');
                if (caretIcon) {
                  const iconClosed = caretIcon.querySelector('.icon-closed');
                  const iconOpen = caretIcon.querySelector('.icon-open');
                  
                  if (iconClosed && iconOpen) {
                    // Animate the icon transition
                    iconClosed.style.opacity = '0';
                    iconOpen.style.opacity = '1';
                    
                    setTimeout(() => {
                      iconClosed.style.display = 'none';
                      iconOpen.style.display = 'block';
                    }, 150);
                  }
                }
              }
              accordion.removeAttribute('open');
              
              // Reset the position of items below this accordion
              this.adjustItemsBelow(accordion, false);
              
              // Remove animation classes after transition
              setTimeout(() => {
                menuItems.forEach(item => {
                  item.classList.remove('menu-item-exiting');
                  item.style.transitionDelay = '';
                });
              }, 300);
            }, 300); // Allow time for staggered animations
          } else {
            // No submenu, just close it directly
            if (summary) {
              summary.setAttribute('aria-expanded', 'false');
              const caretIcon = summary.querySelector('.accordion-icon');
              if (caretIcon) {
                const iconClosed = caretIcon.querySelector('.icon-closed');
                const iconOpen = caretIcon.querySelector('.icon-open');
                
                if (iconClosed && iconOpen) {
                  // Fade transition for icons
                  iconClosed.style.opacity = '0';
                  iconOpen.style.opacity = '1';
                  
                  setTimeout(() => {
                    iconClosed.style.display = 'none';
                    iconOpen.style.display = 'block';
                  }, 150);
                }
              }
            }
            accordion.removeAttribute('open');
            
            // Reset the position of items below this accordion
            this.adjustItemsBelow(accordion, false);
          }
        }
      });
    }
    
    // New method to adjust items below an opening/closing accordion
    adjustItemsBelow(accordion, isOpening) {
      // Find all items that come after this accordion
      const parent = accordion.parentElement;
      if (!parent) return;
      
      const menuItems = Array.from(parent.children);
      const accordionIndex = menuItems.indexOf(accordion);
      
      if (accordionIndex === -1) return;
      
      // Get the height change amount
      const submenu = accordion.querySelector('.menu-drawer__submenu');
      if (!submenu) return;
      
      // For items after this accordion, add transition class and adjust position
      for (let i = accordionIndex + 1; i < menuItems.length; i++) {
        const item = menuItems[i];
        
        // Add a class to enable smooth transitions
        item.classList.add('is-shifting');
        
        if (isOpening) {
          // When opening, we fade these items slightly during transition
          item.style.opacity = '0.9';
          
          // After transition completes, restore opacity
          setTimeout(() => {
            item.style.opacity = '1';
            
            // Remove transition class after animation completes
            setTimeout(() => {
              item.classList.remove('is-shifting');
            }, 300);
          }, 250);
        } else {
          // When closing, we don't need to fade - just add the transition class temporarily
          setTimeout(() => {
            item.classList.remove('is-shifting');
          }, 300);
        }
      }
    }
    
    handleSummaryClick(event) {
      // Prevent multiple clicks during animation
      if (this.animationInProgress) return;
      this.animationInProgress = true;
      
      // Prevent default behavior that would automatically open/close the <details>
      event.preventDefault();
      event.stopPropagation();
      
      const summary = event.currentTarget;
      const detailsElement = summary.parentNode;
      const submenu = detailsElement.querySelector('.menu-drawer__submenu');
      
      if (!submenu) {
        this.animationInProgress = false;
        return;
      }
      
      const isOpen = detailsElement.hasAttribute('open');
      
      // Get the icon elements
      const caretIcon = summary.querySelector('.accordion-icon');
      const iconClosed = caretIcon ? caretIcon.querySelector('.icon-closed') : null;
      const iconOpen = caretIcon ? caretIcon.querySelector('.icon-open') : null;
      
      if (!isOpen) {
        // OPENING: First close all other accordions
        this.closeAllAccordions(detailsElement);
        
        // Mark as open before animation
        detailsElement.setAttribute('open', '');
        summary.setAttribute('aria-expanded', 'true');
        
        // Add is-open class to trigger CSS animations
        submenu.classList.add('is-open');
        
        // Update icon display with animation
        if (iconClosed && iconOpen) {
          iconOpen.style.opacity = '0';
          iconClosed.style.opacity = '1';
          
          setTimeout(() => {
            iconOpen.style.display = 'none';
            iconClosed.style.display = 'block';
          }, 150);
        }
        
        // Animate each menu item with staggered delay
        const menuItems = submenu.querySelectorAll('.menu-drawer__menu > li');
        menuItems.forEach((item, index) => {
          // Set submenu animation order
          item.style.setProperty('--submenu-order', index + 1);
          
          // Add entering class for CSS animations
          item.classList.add('menu-item-entering');
          
          // Reset initial state
          item.style.opacity = '0';
          item.style.transform = 'translateY(8px)';
          
          // Set staggered delay
          const delay = 0.05 * (index + 1);
          item.style.transitionDelay = `${delay}s`;
          
          // Trigger reflow to ensure animation works
          void item.offsetWidth;
          
          // Start animation
          item.style.opacity = '1';
          item.style.transform = 'translateY(0)';
        });
        
        // Adjust items below this accordion
        this.adjustItemsBelow(detailsElement, true);
        
        // Reset animation flag and clean up after animation completes
        setTimeout(() => {
          menuItems.forEach(item => {
            item.style.transitionDelay = '';
          });
          
          this.animationInProgress = false;
        }, 500);
      } else {
        // CLOSING: Animate items out first
        const menuItems = submenu.querySelectorAll('.menu-drawer__menu > li');
        
        menuItems.forEach((item, index) => {
          // Add animation-out class
          item.classList.remove('menu-item-entering');
          item.classList.add('menu-item-exiting');
          
          // Reverse animation order for closing (last item first)
          const delay = 0.03 * (menuItems.length - index - 1);
          item.style.transitionDelay = `${delay}s`;
          item.style.opacity = '0';
          item.style.transform = 'translateY(8px)';
        });
        
        // Update icon with animation
        if (iconClosed && iconOpen) {
          iconClosed.style.opacity = '0';
          iconOpen.style.opacity = '1';
          
          setTimeout(() => {
            iconClosed.style.display = 'none';
            iconOpen.style.display = 'block';
          }, 150);
        }
        
        // After animation completes, close the accordion
        setTimeout(() => {
          submenu.classList.remove('is-open');
          detailsElement.removeAttribute('open');
          summary.setAttribute('aria-expanded', 'false');
          
          // Adjust items below this accordion
          this.adjustItemsBelow(detailsElement, false);
          
          // Remove animation classes and reset
          menuItems.forEach(item => {
            item.classList.remove('menu-item-exiting');
            item.style.transitionDelay = '';
          });
          
          // Reset animation flag
          this.animationInProgress = false;
        }, 300);
      }
    }
    
    // Add hover effects to menu items
    addHoverEffect() {
      const menuItems = document.querySelectorAll('.menu-drawer__menu > li > a.menu-drawer__menu-item');
      menuItems.forEach(item => {
        // Only apply if it's not an accordion item
        if (!item.closest('details.accordion-menu-item')) {
          item.addEventListener('mouseenter', () => this.handleMouseEnter(item));
          item.addEventListener('mouseleave', () => this.handleMouseLeave(item));
        }
      });
    }
  }

  window.MenuDrawerAccordion = MenuDrawerAccordion;
}

// Initialize the accordion menu when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  window.menuDrawerAccordion = new MenuDrawerAccordion();
  // Add hover effects after a short delay to avoid initial animation conflicts
  setTimeout(() => {
    window.menuDrawerAccordion.addHoverEffect();
  }, 1000);
});